import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { PageHead } from "../../components/ui-bits";
import { SERVICE_LIST } from "../../lib/services";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, UsersThree } from "@phosphor-icons/react";

const EMPTY = { name: "", role: "", email: "", phone: "", avatar: "", services: [] };
const inputCls = "w-full border border-black/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]";

function MemberForm({ init, onSave, onCancel }) {
  const [f, setF] = useState(init);
  const toggle = (s) => setF({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} data-testid="team-form" className="card p-6 mb-6 fade-up">
      <div className="grid sm:grid-cols-3 gap-3 mb-3">
        {[["name", "Name"], ["role", "Designation (e.g. Chartered Accountant)"], ["email", "Email"], ["phone", "Phone"], ["avatar", "Photo URL (optional)"]].map(([k, l]) => (
          <div key={k}><label className="label block mb-1">{l}</label>
            <input data-testid={`team-f-${k}`} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={inputCls} /></div>
        ))}
      </div>
      <p className="label mb-2">Services handled</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {SERVICE_LIST.map((s) => (
          <button type="button" key={s} data-testid={`team-svc-${s}`} onClick={() => toggle(s)}
            className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${f.services.includes(s) ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-black/20 hover:bg-black/5"}`}>{s}</button>
        ))}
      </div>
      <div className="flex gap-2">
        <button data-testid="team-save" className="bg-[#002FA7] hover:bg-[#00227A] text-white px-4 py-2 rounded-sm text-sm transition-colors">Save member</button>
        <button type="button" onClick={onCancel} className="border border-black/20 px-4 py-2 rounded-sm text-sm hover:bg-black/5">Cancel</button>
      </div>
    </form>
  );
}

export default function AdminTeam() {
  const [team, setTeam] = useState([]);
  const [clients, setClients] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/admin/team").then((r) => setTeam(r.data));
    api.get("/admin/clients").then((r) => setClients(r.data));
  };
  useEffect(() => { load(); }, []);

  const save = async (f) => {
    if (!f.name.trim() || !f.role.trim()) { toast.error("Name and designation are required"); return; }
    const { id, client_emails, created_at, ...body } = f;
    if (id) await api.patch(`/admin/team/${id}`, body); else await api.post("/admin/team", body);
    toast.success("Team member saved"); setEditing(null); load();
  };
  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the team?`)) return;
    await api.delete(`/admin/team/${m.id}`); toast.success("Removed"); load();
  };
  const toggleClient = async (m, email) => {
    const cur = m.client_emails || [];
    const next = cur.includes(email) ? cur.filter((e) => e !== email) : [...cur, email];
    await api.put(`/admin/team/${m.id}/clients`, { client_emails: next });
    toast.success("Allocation updated"); load();
  };

  return (
    <div>
      <PageHead eyebrow="Maintain Team & Client Allocation" title="Team Directory">
        <button data-testid="team-add-btn" onClick={() => setEditing({ ...EMPTY })} className="bg-[#002FA7] hover:bg-[#00227A] text-white px-4 py-2 rounded-sm text-sm flex items-center gap-2 transition-colors"><Plus size={16} /> Add Member</button>
      </PageHead>
      <p className="text-sm text-[#555] mb-4">Clients only see the team members allocated to them (plus their assigned CFO agent).</p>
      {editing && <MemberForm key={editing.id || "new"} init={{ ...EMPTY, ...editing }} onSave={save} onCancel={() => setEditing(null)} />}
      {team.length === 0 && !editing && (
        <div className="card p-8 text-center"><UsersThree size={32} className="mx-auto mb-2 text-[#bbb]" /><p className="text-sm text-[#888]">No team members yet. Add your first professional.</p></div>
      )}
      <div className="space-y-4">
        {team.map((m, i) => (
          <div key={m.id} data-testid={`team-member-${i}`} className="card p-5">
            <div className="flex flex-wrap items-start gap-4 mb-3">
              <div className="w-12 h-12 rounded-sm bg-[#002FA7] text-white flex items-center justify-center font-head font-black">{m.name[0]}</div>
              <div className="flex-1 min-w-[200px]">
                <p className="font-head font-bold">{m.name} <span className="label ml-2" style={{ color: "#002FA7" }}>{m.role}</span></p>
                <p className="text-xs text-[#888]">{[m.email, m.phone].filter(Boolean).join(" · ") || "—"}</p>
                {(m.services || []).length > 0 && <p className="text-xs text-[#555] mt-1">{m.services.join(" · ")}</p>}
              </div>
              <button data-testid={`team-edit-${i}`} onClick={() => setEditing(m)} className="border border-black/20 hover:bg-black/5 p-2 rounded-sm"><PencilSimple size={15} /></button>
              <button data-testid={`team-del-${i}`} onClick={() => remove(m)} className="border border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/5 p-2 rounded-sm"><Trash size={15} /></button>
            </div>
            <p className="label mb-2">Allocated to clients ({(m.client_emails || []).length}) — click to toggle</p>
            <div className="flex flex-wrap gap-2">
              {clients.map((c) => {
                const on = (m.client_emails || []).includes(c.email);
                return (
                  <button key={c.email} data-testid={`team-alloc-${i}-${c.email}`} onClick={() => toggleClient(m, c.email)}
                    className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${on ? "bg-[#00A86B] text-white border-[#00A86B]" : "border-black/20 hover:bg-black/5"}`}>{c.company || c.email}</button>
                );
              })}
              {clients.length === 0 && <span className="text-xs text-[#888]">No clients yet.</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
